/**
 * docxExport.js - Word document export engine for OptimalAvailability Studio
 * Generates maintenance plans, work instructions, FMEA/FMECA, and availability reports
 * as styled print-ready HTML for Electron PDF export
 *
 * @module engine/docxExport
 * @exports DocxExport
 */

class DocxExport {
  /**
   * Optimal branding colors
   * @static
   */
  static BRAND_COLORS = {
    NAVY: '#1B2A4A',
    GOLD: '#E8A838',
    BLUE: '#2D5F8A',
    LIGHT_GRAY: '#F5F5F5',
    TEXT_DARK: '#333333',
  };

  /**
   * Page styles for printable HTML
   * @static
   */
  static PAGE_STYLES = `
    @media print {
      body { margin: 0; padding: 0; }
      .page-break { page-break-after: always; }
      .no-break { page-break-inside: avoid; }
    }
    html, body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 11pt;
      color: ${DocxExport.BRAND_COLORS.TEXT_DARK};
      line-height: 1.5;
      margin: 0;
      padding: 20px;
      background: white;
    }
    h1 {
      color: ${DocxExport.BRAND_COLORS.NAVY};
      font-size: 24pt;
      margin-top: 20px;
      margin-bottom: 10px;
      border-bottom: 3px solid ${DocxExport.BRAND_COLORS.GOLD};
      padding-bottom: 10px;
    }
    h2 {
      color: ${DocxExport.BRAND_COLORS.BLUE};
      font-size: 16pt;
      margin-top: 16px;
      margin-bottom: 8px;
    }
    h3 {
      color: ${DocxExport.BRAND_COLORS.BLUE};
      font-size: 13pt;
      margin-top: 12px;
      margin-bottom: 6px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;
    }
    th {
      background-color: ${DocxExport.BRAND_COLORS.NAVY};
      color: white;
      padding: 10px;
      text-align: left;
      font-weight: bold;
      border: 1px solid ${DocxExport.BRAND_COLORS.BLUE};
    }
    td {
      padding: 8px 10px;
      border: 1px solid #CCCCCC;
    }
    tr:nth-child(even) {
      background-color: ${DocxExport.BRAND_COLORS.LIGHT_GRAY};
    }
    .header-section {
      text-align: center;
      margin-bottom: 30px;
      border-top: 3px solid ${DocxExport.BRAND_COLORS.GOLD};
      border-bottom: 3px solid ${DocxExport.BRAND_COLORS.GOLD};
      padding: 20px 0;
    }
    .title-page {
      height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      page-break-after: always;
    }
    .logo-text {
      color: ${DocxExport.BRAND_COLORS.NAVY};
      font-size: 36pt;
      font-weight: bold;
      margin-bottom: 20px;
    }
    .metadata {
      margin: 10px 0;
      font-size: 11pt;
    }
    .metadata-label {
      font-weight: bold;
      color: ${DocxExport.BRAND_COLORS.BLUE};
      display: inline-block;
      width: 100px;
    }
    .table-of-contents {
      margin: 20px 0;
      padding: 20px;
      background-color: ${DocxExport.BRAND_COLORS.LIGHT_GRAY};
      border-left: 4px solid ${DocxExport.BRAND_COLORS.GOLD};
    }
    .toc-item {
      margin: 6px 0;
      padding-left: 20px;
    }
    .safety-warning {
      background-color: #FFF3CD;
      border-left: 4px solid #FF6B6B;
      padding: 12px;
      margin: 12px 0;
      font-weight: bold;
      color: #856404;
    }
    .step {
      margin: 12px 0;
      padding: 10px;
      border-left: 3px solid ${DocxExport.BRAND_COLORS.BLUE};
      background-color: ${DocxExport.BRAND_COLORS.LIGHT_GRAY};
    }
    .step-number {
      display: inline-block;
      background-color: ${DocxExport.BRAND_COLORS.NAVY};
      color: white;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      text-align: center;
      line-height: 28px;
      margin-right: 10px;
      font-weight: bold;
    }
    .signature-block {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #999;
    }
    .signature-line {
      display: inline-block;
      margin-right: 40px;
      margin-top: 20px;
    }
    .line {
      border-bottom: 1px solid #000;
      width: 200px;
      display: inline-block;
    }
    .label {
      font-size: 9pt;
      display: block;
      margin-top: 5px;
    }
    .cost-summary {
      background-color: ${DocxExport.BRAND_COLORS.LIGHT_GRAY};
      padding: 15px;
      margin: 15px 0;
      border-left: 4px solid ${DocxExport.BRAND_COLORS.GOLD};
    }
    .rpn-critical {
      background-color: #FFE5E5;
      color: #B71C1C;
      font-weight: bold;
    }
    .rpn-high {
      background-color: #FFF4E5;
      color: #D97706;
      font-weight: bold;
    }
    .rpn-medium {
      background-color: #FEF3C7;
      color: #92400E;
    }
  `;

  /**
   * Generate a comprehensive maintenance plan document
   *
   * @param {Object} projectData - Project information and analysis results
   * @param {string} projectData.projectName - Project name
   * @param {string} projectData.projectId - Project identifier
   * @param {string} projectData.author - Document author
   * @param {Array<Object>} projectData.equipment - Equipment list
   * @param {Array<Object>} projectData.scheduledTasks - Scheduled maintenance tasks from RCM
   * @param {Array<Object>} projectData.correctiveTasks - Corrective maintenance tasks
   * @param {Array<Object>} projectData.spareParts - Spare parts requirements
   * @param {Object} projectData.costAnalysis - Cost breakdown
   * @param {Object} options - Export options
   * @param {boolean} options.includeEquipmentSummary - Include equipment table
   * @param {boolean} options.includeSpares - Include spare parts
   * @returns {Object} { content: string (HTML), filename: string }
   */
  generateMaintenancePlan(projectData, options = {}) {
    const defaults = {
      includeEquipmentSummary: true,
      includeSpares: true,
      includeRiskAnalysis: true,
    };
    const opts = { ...defaults, ...options };

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = this._htmlHeader();

    // Title Page
    html += `
      <div class="title-page page-break">
        <div class="logo-text">OPTIMAL</div>
        <h1 style="font-size: 28pt; border: none;">Maintenance Plan</h1>
        <div style="margin-top: 40px; font-size: 18pt; font-weight: bold;">
          ${projectData.projectName || 'Untitled Project'}
        </div>
        <div class="metadata" style="margin-top: 40px; font-size: 13pt;">
          <div class="metadata-label">Project ID:</div> ${projectData.projectId || 'N/A'}<br>
          <div class="metadata-label">Date:</div> ${dateStr}<br>
          <div class="metadata-label">Author:</div> ${projectData.author || 'OptimalAvailability Studio'}<br>
        </div>
      </div>
    `;

    // Table of Contents
    html += `
      <div class="page-break">
        <h1>Table of Contents</h1>
        <div class="table-of-contents">
          <div class="toc-item">1. Equipment Summary</div>
          <div class="toc-item">2. Scheduled Maintenance Tasks</div>
          <div class="toc-item">3. Corrective Maintenance Tasks</div>
          ${opts.includeSpares ? '<div class="toc-item">4. Spare Parts Requirements</div>' : ''}
          ${opts.includeRiskAnalysis ? '<div class="toc-item">5. Risk Analysis Summary</div>' : ''}
          <div class="toc-item">${opts.includeSpares && opts.includeRiskAnalysis ? '6' : opts.includeSpares || opts.includeRiskAnalysis ? '5' : '4'}. Cost Summary</div>
        </div>
      </div>
    `;

    // Equipment Summary
    if (opts.includeEquipmentSummary && projectData.equipment && projectData.equipment.length > 0) {
      html += `
        <div class="page-break">
          <h1>1. Equipment Summary</h1>
          <table>
            <thead>
              <tr>
                <th>Equipment ID</th>
                <th>Name</th>
                <th>Location</th>
                <th>Type</th>
                <th>Failure Rate (λ)</th>
                <th>MTBF (hours)</th>
              </tr>
            </thead>
            <tbody>
      `;
      projectData.equipment.forEach(eq => {
        const mtbf = eq.mtbf ? Math.round(eq.mtbf).toLocaleString() : 'N/A';
        html += `
          <tr>
            <td>${eq.id || ''}</td>
            <td>${eq.name || ''}</td>
            <td>${eq.location || ''}</td>
            <td>${eq.type || ''}</td>
            <td>${eq.failureRate ? eq.failureRate.toExponential(2) : 'N/A'}</td>
            <td>${mtbf}</td>
          </tr>
        `;
      });
      html += `
            </tbody>
          </table>
        </div>
      `;
    }

    // Scheduled Maintenance Tasks
    if (projectData.scheduledTasks && projectData.scheduledTasks.length > 0) {
      html += `
        <div class="page-break">
          <h1>2. Scheduled Maintenance Tasks</h1>
          <table>
            <thead>
              <tr>
                <th>Task ID</th>
                <th>Task Description</th>
                <th>Equipment</th>
                <th>Interval (hours)</th>
                <th>Resources</th>
                <th>Est. Cost</th>
                <th>Criticality</th>
              </tr>
            </thead>
            <tbody>
      `;
      projectData.scheduledTasks.forEach(task => {
        const cost = task.estimatedCost ? `$${task.estimatedCost.toFixed(2)}` : 'TBD';
        html += `
          <tr>
            <td>${task.id || ''}</td>
            <td>${task.description || ''}</td>
            <td>${task.equipment || ''}</td>
            <td>${task.interval || ''}</td>
            <td>${task.resources || ''}</td>
            <td>${cost}</td>
            <td>${task.criticality || 'Medium'}</td>
          </tr>
        `;
      });
      html += `
            </tbody>
          </table>
        </div>
      `;
    }

    // Corrective Maintenance Tasks
    if (projectData.correctiveTasks && projectData.correctiveTasks.length > 0) {
      html += `
        <div class="page-break">
          <h1>3. Corrective Maintenance Tasks</h1>
          <table>
            <thead>
              <tr>
                <th>Failure Mode</th>
                <th>Equipment</th>
                <th>Detection Method</th>
                <th>Corrective Action</th>
                <th>Est. Cost</th>
              </tr>
            </thead>
            <tbody>
      `;
      projectData.correctiveTasks.forEach(task => {
        const cost = task.estimatedCost ? `$${task.estimatedCost.toFixed(2)}` : 'TBD';
        html += `
          <tr>
            <td>${task.failureMode || ''}</td>
            <td>${task.equipment || ''}</td>
            <td>${task.detectionMethod || ''}</td>
            <td>${task.action || ''}</td>
            <td>${cost}</td>
          </tr>
        `;
      });
      html += `
            </tbody>
          </table>
        </div>
      `;
    }

    // Spare Parts
    if (opts.includeSpares && projectData.spareParts && projectData.spareParts.length > 0) {
      html += `
        <div class="page-break">
          <h1>4. Spare Parts Requirements</h1>
          <table>
            <thead>
              <tr>
                <th>Part ID</th>
                <th>Part Name</th>
                <th>Equipment</th>
                <th>Qty Required</th>
                <th>Unit Cost</th>
                <th>Reorder Lead Time</th>
              </tr>
            </thead>
            <tbody>
      `;
      projectData.spareParts.forEach(part => {
        const cost = part.unitCost ? `$${part.unitCost.toFixed(2)}` : 'TBD';
        html += `
          <tr>
            <td>${part.id || ''}</td>
            <td>${part.name || ''}</td>
            <td>${part.equipment || ''}</td>
            <td>${part.quantity || ''}</td>
            <td>${cost}</td>
            <td>${part.leadTime || ''}</td>
          </tr>
        `;
      });
      html += `
            </tbody>
          </table>
        </div>
      `;
    }

    // Cost Summary
    html += this._generateCostSummary(projectData, 'page-break');

    html += `
      </body>
      </html>
    `;

    const filename = `${projectData.projectName || 'MaintenancePlan'}_${now.toISOString().split('T')[0]}.html`;

    return {
      content: html,
      filename,
    };
  }

  /**
   * Generate a work instruction document
   *
   * @param {Object} task - Task/procedure information
   * @param {string} task.taskId - Task identifier
   * @param {string} task.taskName - Task name/title
   * @param {string} task.description - Task description
   * @param {Array<string>} task.procedure - Numbered procedure steps
   * @param {Array<string>} task.safetyWarnings - Safety precautions
   * @param {Array<string>} task.toolsRequired - Tools needed
   * @param {Array<string>} task.materialsRequired - Materials needed
   * @param {Array<string>} task.qualityChecks - Quality verification steps
   * @param {Object} equipment - Equipment being serviced
   * @param {string} equipment.id - Equipment ID
   * @param {string} equipment.name - Equipment name
   * @param {string} equipment.location - Equipment location
   * @param {Object} options - Export options
   * @param {number} options.revision - Document revision number
   * @param {string} options.skillLevel - Required skill level (Basic, Intermediate, Advanced)
   * @param {string} options.estimatedDuration - Task duration estimate
   * @returns {Object} { content: string (HTML), filename: string }
   */
  generateWorkInstruction(task, equipment, options = {}) {
    const opts = {
      revision: options.revision || 1,
      skillLevel: options.skillLevel || 'Intermediate',
      estimatedDuration: options.estimatedDuration || 'TBD',
    };

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = this._htmlHeader();

    html += `
      <div class="header-section">
        <h1>${task.taskName || 'Work Instruction'}</h1>
        <div class="metadata">
          <div><span class="metadata-label">Equipment:</span> ${equipment?.name || 'N/A'}</div>
          <div><span class="metadata-label">Location:</span> ${equipment?.location || 'N/A'}</div>
          <div><span class="metadata-label">Revision:</span> ${opts.revision}</div>
          <div><span class="metadata-label">Date:</span> ${dateStr}</div>
          <div><span class="metadata-label">Skill Level:</span> ${opts.skillLevel}</div>
          <div><span class="metadata-label">Est. Duration:</span> ${opts.estimatedDuration}</div>
        </div>
      </div>

      <h2>Task Description</h2>
      <p>${task.description || 'See procedure steps below.'}</p>
    `;

    // Safety Warnings
    if (task.safetyWarnings && task.safetyWarnings.length > 0) {
      html += '<h2>Safety Warnings & Precautions</h2>';
      task.safetyWarnings.forEach(warning => {
        html += `<div class="safety-warning">⚠ ${warning}</div>`;
      });
    }

    // Tools Required
    if (task.toolsRequired && task.toolsRequired.length > 0) {
      html += `
        <h2>Tools & Materials Required</h2>
        <h3>Tools:</h3>
        <ul>
      `;
      task.toolsRequired.forEach(tool => {
        html += `<li>${tool}</li>`;
      });
      html += '</ul>';
    }

    // Materials Required
    if (task.materialsRequired && task.materialsRequired.length > 0) {
      html += `
        <h3>Materials:</h3>
        <ul>
      `;
      task.materialsRequired.forEach(material => {
        html += `<li>${material}</li>`;
      });
      html += '</ul>';
    }

    // Procedure Steps
    if (task.procedure && task.procedure.length > 0) {
      html += '<h2>Procedure</h2>';
      task.procedure.forEach((step, index) => {
        html += `
          <div class="step no-break">
            <span class="step-number">${index + 1}</span>
            <strong>${step.title || ''}</strong>
            <p>${step.description || step}</p>
          </div>
        `;
      });
    }

    // Quality Checks
    if (task.qualityChecks && task.qualityChecks.length > 0) {
      html += `
        <h2>Quality Checks</h2>
        <table>
          <thead>
            <tr>
              <th>Check</th>
              <th>Acceptance Criteria</th>
              <th>Pass/Fail</th>
            </tr>
          </thead>
          <tbody>
      `;
      task.qualityChecks.forEach(check => {
        html += `
          <tr>
            <td>${check.description || ''}</td>
            <td>${check.criteria || ''}</td>
            <td style="text-align: center;">☐ Pass  ☐ Fail</td>
          </tr>
        `;
      });
      html += `
          </tbody>
        </table>
      `;
    }

    // Sign-off Section
    html += `
      <div class="signature-block">
        <h2>Sign-Off</h2>
        <div class="signature-line">
          <div class="line"></div>
          <div class="label">Technician Name & Signature</div>
        </div>
        <div class="signature-line">
          <div class="line"></div>
          <div class="label">Date</div>
        </div>
        <div style="clear: both; margin-top: 20px;">
          <div class="signature-line">
            <div class="line"></div>
            <div class="label">Supervisor Name & Signature</div>
          </div>
          <div class="signature-line">
            <div class="line"></div>
            <div class="label">Date</div>
          </div>
        </div>
      </div>
    `;

    html += `
      </body>
      </html>
    `;

    const filename = `${task.taskName || 'WorkInstruction'}_${now.toISOString().split('T')[0]}.html`;

    return {
      content: html,
      filename,
    };
  }

  /**
   * Generate FMEA (Failure Mode and Effects Analysis) report
   *
   * @param {Array<Object>} rcmCauses - RCM analysis results with failure modes
   * @param {Object} options - Export options
   * @param {string} options.systemName - System/equipment name
   * @param {string} options.team - Analysis team
   * @returns {Object} { content: string (HTML), filename: string }
   */
  generateFMEAReport(rcmCauses, options = {}) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = this._htmlHeader();

    html += `
      <div class="header-section">
        <h1>FMEA Report</h1>
        <div class="metadata">
          <div><span class="metadata-label">System:</span> ${options.systemName || 'N/A'}</div>
          <div><span class="metadata-label">Date:</span> ${dateStr}</div>
          <div><span class="metadata-label">Team:</span> ${options.team || 'Analysis Team'}</div>
        </div>
      </div>

      <p>
        Failure Mode and Effects Analysis (FMEA) is a systematic approach to identifying and prioritizing
        potential failure modes and their effects on system performance.
      </p>

      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Function</th>
            <th>Failure Mode</th>
            <th>Failure Effect</th>
            <th>Severity (S)</th>
            <th>Occurrence (O)</th>
            <th>Detection (D)</th>
            <th>RPN</th>
            <th>Recommended Action</th>
            <th>Responsibility</th>
          </tr>
        </thead>
        <tbody>
    `;

    if (rcmCauses && rcmCauses.length > 0) {
      rcmCauses.forEach(cause => {
        const severity = cause.severity || 5;
        const occurrence = cause.occurrence || 5;
        const detection = cause.detection || 5;
        const rpn = severity * occurrence * detection;

        let rpmClass = '';
        if (rpn >= 100) rpmClass = 'rpn-critical';
        else if (rpn >= 50) rpmClass = 'rpn-high';
        else rpmClass = 'rpn-medium';

        html += `
          <tr>
            <td>${cause.equipment || ''}</td>
            <td>${cause.function || ''}</td>
            <td>${cause.failureMode || ''}</td>
            <td>${cause.effect || ''}</td>
            <td style="text-align: center;">${severity}</td>
            <td style="text-align: center;">${occurrence}</td>
            <td style="text-align: center;">${detection}</td>
            <td style="text-align: center;" class="${rpmClass}">${rpn}</td>
            <td>${cause.recommendedAction || ''}</td>
            <td>${cause.responsibility || ''}</td>
          </tr>
        `;
      });
    }

    html += `
        </tbody>
      </table>

      <h2>RPN Rating Scale</h2>
      <p>
        <strong>RPN ≥ 100 (Critical):</strong> Immediate action required. High risk of significant failure impact.<br>
        <strong>RPN 50-99 (High):</strong> Action recommended. Moderate to high risk.<br>
        <strong>RPN &lt; 50 (Medium/Low):</strong> Monitor. Lower priority but track for changes.
      </p>
    `;

    html += `
      </body>
      </html>
    `;

    const filename = `FMEA_Report_${now.toISOString().split('T')[0]}.html`;

    return {
      content: html,
      filename,
    };
  }

  /**
   * Generate FMECA (Failure Mode, Effects, and Criticality Analysis) report per MIL-STD-1629A
   *
   * @param {Array<Object>} rcmCauses - RCM analysis results
   * @param {Array<Object>} consequences - Consequence analysis data
   * @param {Object} options - Export options
   * @returns {Object} { content: string (HTML), filename: string }
   */
  generateFMECAReport(rcmCauses, consequences, options = {}) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = this._htmlHeader();

    html += `
      <div class="header-section">
        <h1>FMECA Report</h1>
        <h3>MIL-STD-1629A Failure Mode, Effects, and Criticality Analysis</h3>
        <div class="metadata">
          <div><span class="metadata-label">Date:</span> ${dateStr}</div>
          <div><span class="metadata-label">Revision:</span> 1.0</div>
        </div>
      </div>

      <p>
        Failure Mode, Effects, and Criticality Analysis (FMECA) extends FMEA with quantitative criticality
        metrics and follows the military standard MIL-STD-1629A for systematic reliability analysis.
      </p>

      <table>
        <thead>
          <tr>
            <th>Item ID</th>
            <th>Item Name</th>
            <th>Function</th>
            <th>Failure Mode</th>
            <th>Local Effect</th>
            <th>Next Higher Effect</th>
            <th>End Effect</th>
            <th>Severity</th>
            <th>Failure Rate (β)</th>
            <th>Mode Ratio (α)</th>
            <th>Criticality (Cm)</th>
          </tr>
        </thead>
        <tbody>
    `;

    if (rcmCauses && rcmCauses.length > 0) {
      rcmCauses.forEach((cause, idx) => {
        const failureRate = cause.failureRate || 0.001;
        const modeRatio = cause.modeRatio || 0.5;
        const criticality = failureRate * modeRatio * 1000; // Normalized to 0-1000 scale

        html += `
          <tr>
            <td>${idx + 1}</td>
            <td>${cause.equipment || ''}</td>
            <td>${cause.function || ''}</td>
            <td>${cause.failureMode || ''}</td>
            <td>${cause.localEffect || ''}</td>
            <td>${cause.nextHigherEffect || ''}</td>
            <td>${cause.endEffect || ''}</td>
            <td>${cause.severity || 'Medium'}</td>
            <td>${failureRate.toExponential(2)}</td>
            <td>${modeRatio.toFixed(2)}</td>
            <td>${criticality.toFixed(2)}</td>
          </tr>
        `;
      });
    }

    html += `
        </tbody>
      </table>

      <h2>Criticality Assessment</h2>
      <p>
        The Criticality (Cm) value is calculated as: <strong>Cm = β × α × 1000</strong><br>
        Where β is the failure rate and α is the mode ratio (proportion of failures in this mode).
      </p>

      <h2>Severity Classification</h2>
      <ul>
        <li><strong>Catastrophic:</strong> Loss of system/mission; potential injury or death</li>
        <li><strong>Critical:</strong> Severe degradation or loss of major function; injury possible</li>
        <li><strong>Major:</strong> Degradation of primary function; no injury likely</li>
        <li><strong>Minor:</strong> Minimal impact on performance; no safety concern</li>
      </ul>
    `;

    html += `
      </body>
      </html>
    `;

    const filename = `FMECA_Report_MIL-STD-1629A_${now.toISOString().split('T')[0]}.html`;

    return {
      content: html,
      filename,
    };
  }

  /**
   * Generate availability analysis report from simulation results
   *
   * @param {Object} simResults - Simulation results from availability analysis
   * @param {Object} projectData - Project data
   * @param {Object} options - Export options
   * @returns {Object} { content: string (HTML), filename: string }
   */
  generateAvailabilityReport(simResults, projectData, options = {}) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = this._htmlHeader();

    html += `
      <div class="header-section">
        <h1>Availability Analysis Report</h1>
        <div class="metadata">
          <div><span class="metadata-label">Project:</span> ${projectData.projectName || 'N/A'}</div>
          <div><span class="metadata-label">Date:</span> ${dateStr}</div>
        </div>
      </div>

      <h2>Executive Summary</h2>
      <div class="cost-summary">
        <p><strong>System Availability:</strong> ${(simResults.availability * 100).toFixed(2)}%</p>
        <p><strong>MTBF (Mean Time Between Failures):</strong> ${(simResults.mtbf || 0).toFixed(2)} hours</p>
        <p><strong>MTTR (Mean Time To Repair):</strong> ${(simResults.mttr || 0).toFixed(2)} hours</p>
        <p><strong>Annual Downtime:</strong> ${(simResults.annualDowntime || 0).toFixed(1)} hours</p>
      </div>

      <h2>Results by Equipment</h2>
      <table>
        <thead>
          <tr>
            <th>Equipment</th>
            <th>Availability</th>
            <th>MTBF (hours)</th>
            <th>MTTR (hours)</th>
            <th>Annual Failures</th>
          </tr>
        </thead>
        <tbody>
    `;

    if (simResults.equipmentResults && simResults.equipmentResults.length > 0) {
      simResults.equipmentResults.forEach(eq => {
        html += `
          <tr>
            <td>${eq.name || ''}</td>
            <td>${(eq.availability * 100).toFixed(2)}%</td>
            <td>${(eq.mtbf || 0).toFixed(2)}</td>
            <td>${(eq.mttr || 0).toFixed(2)}</td>
            <td>${(eq.annualFailures || 0).toFixed(2)}</td>
          </tr>
        `;
      });
    }

    html += `
        </tbody>
      </table>

      <h2>Maintenance Strategy Impact</h2>
      <p>
        The current maintenance strategy results in ${(simResults.availability * 100).toFixed(2)}% system availability.
        Total annual downtime is approximately ${(simResults.annualDowntime || 0).toFixed(1)} hours.
      </p>

      <h2>Recommendations</h2>
      <ul>
        <li>Review scheduled maintenance intervals for low-availability equipment</li>
        <li>Consider preventive maintenance optimization</li>
        <li>Evaluate spare parts inventory levels</li>
        <li>Assess maintenance team resource allocation</li>
      </ul>
    `;

    html += `
      </body>
      </html>
    `;

    const filename = `AvailabilityReport_${now.toISOString().split('T')[0]}.html`;

    return {
      content: html,
      filename,
    };
  }

  /**
   * Generate Weibull analysis report
   *
   * @param {Object} weibullData - Weibull distribution analysis results
   * @param {Object} options - Export options
   * @returns {Object} { content: string (HTML), filename: string }
   */
  generateWeibullReport(weibullData, options = {}) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    let html = this._htmlHeader();

    html += `
      <div class="header-section">
        <h1>Weibull Analysis Report</h1>
        <div class="metadata">
          <div><span class="metadata-label">Date:</span> ${dateStr}</div>
          <div><span class="metadata-label">Equipment:</span> ${weibullData.equipment || 'N/A'}</div>
        </div>
      </div>

      <h2>Distribution Parameters</h2>
      <div class="cost-summary">
        <p><strong>Shape Parameter (k):</strong> ${(weibullData.k || 0).toFixed(3)}</p>
        <p><strong>Scale Parameter (λ):</strong> ${(weibullData.lambda || 0).toFixed(3)}</p>
        <p><strong>R² Goodness of Fit:</strong> ${(weibullData.r2 || 0).toFixed(4)}</p>
      </div>

      <h2>Failure Rate Characteristics</h2>
      <p>
        <strong>Shape Parameter Interpretation:</strong><br>
        k &lt; 1: Decreasing failure rate (infant mortality, early failures)<br>
        k = 1: Constant failure rate (random failures, exponential)<br>
        k &gt; 1: Increasing failure rate (wear-out failures)<br>
        k ≈ 3.6: Approximates normal distribution
      </p>

      <p>
        The current equipment exhibits
        ${weibullData.k < 1 ? 'decreasing failure rates (improving with time)' :
          weibullData.k === 1 ? 'constant failure rates' :
          'increasing failure rates (wear-out phase)'}
        with shape parameter k = ${(weibullData.k || 0).toFixed(3)}.
      </p>

      <h2>Reliability Projections</h2>
      <table>
        <thead>
          <tr>
            <th>Time (hours)</th>
            <th>Reliability R(t)</th>
            <th>Failure Probability F(t)</th>
            <th>Failure Rate λ(t)</th>
          </tr>
        </thead>
        <tbody>
    `;

    const times = [100, 500, 1000, 5000, 10000];
    if (weibullData.projections && Array.isArray(weibullData.projections)) {
      weibullData.projections.slice(0, 5).forEach(proj => {
        html += `
          <tr>
            <td>${proj.time || ''}</td>
            <td>${(proj.reliability || 0).toFixed(4)}</td>
            <td>${(proj.failureProbability || 0).toFixed(4)}</td>
            <td>${(proj.failureRate || 0).toExponential(3)}</td>
          </tr>
        `;
      });
    }

    html += `
        </tbody>
      </table>
    `;

    html += `
      </body>
      </html>
    `;

    const filename = `WeibullReport_${now.toISOString().split('T')[0]}.html`;

    return {
      content: html,
      filename,
    };
  }

  /**
   * Wrap content in full printable HTML with A4 styling and Optimal branding
   *
   * @param {string} reportContent - HTML content to wrap
   * @param {Object} options - Options
   * @param {string} options.title - Document title for <title> tag
   * @returns {string} Complete HTML document
   */
  exportAsHTML(reportContent, options = {}) {
    const title = options.title || 'OptimalAvailability Report';

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
        <style>
          ${DocxExport.PAGE_STYLES}
        </style>
      </head>
      <body>
        ${reportContent}
      </body>
      </html>
    `;
  }

  /**
   * Private helper: Generate HTML document header
   * @private
   */
  _htmlHeader() {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>OptimalAvailability Export</title>
        <style>
          ${DocxExport.PAGE_STYLES}
        </style>
      </head>
      <body>
    `;
  }

  /**
   * Private helper: Generate cost summary section
   * @private
   */
  _generateCostSummary(projectData, classes = '') {
    const costAnalysis = projectData.costAnalysis || {};

    return `
      <div class="${classes}">
        <h1>Cost Summary</h1>
        <div class="cost-summary">
          <table>
            <tr>
              <td><strong>Annual Preventive Maintenance Cost:</strong></td>
              <td style="text-align: right;">$${(costAnalysis.annualPreventive || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td><strong>Annual Corrective Maintenance Cost (estimated):</strong></td>
              <td style="text-align: right;">$${(costAnalysis.annualCorrective || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td><strong>Annual Production Loss (downtime):</strong></td>
              <td style="text-align: right;">$${(costAnalysis.annualDowntimeCost || 0).toFixed(2)}</td>
            </tr>
            <tr style="border-top: 2px solid ${DocxExport.BRAND_COLORS.NAVY}; font-weight: bold;">
              <td><strong>Total Annual Cost:</strong></td>
              <td style="text-align: right;">$${(costAnalysis.totalAnnual || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td colspan="2" style="padding-top: 15px; text-align: center; font-size: 10pt; color: ${DocxExport.BRAND_COLORS.BLUE};">
                Costs based on current maintenance strategy and failure rates.
                Recommendations may reduce total cost through optimized scheduling.
              </td>
            </tr>
          </table>
        </div>
      </div>
    `;
  }
}

export default DocxExport;
